import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { marketplaceAPI, shippingAPI, listingSuppliesAPI, checkoutAPI } from "@/lib/api";
import {
  CATEGORIES, CONDITIONS, CURRENCY_SYMBOLS, brandsForCategory,
} from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, ImagePlus, Loader2, X, Package, Truck, MapPin, ShieldCheck } from "lucide-react";
import ProductSelector from "@/components/ProductSelector";

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

export default function CreateListing() {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = Boolean(editId);
  const [loading, setLoading] = useState(isEdit);
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "",
    brand: "",
    model_name: "",
    condition: "",
    price: "",
    currency: "USD",
    whats_included: "",
    reason_for_selling: "",
    year_purchased: "",
    original_price: "",
    // location
    seller_country: "US",
    seller_zip_code: "",
    seller_city: "",
    seller_state: "",
    location: "",
  });
  const [images, setImages] = useState([]);

  // Shipping toggles
  const [localPickupAvailable, setLocalPickupAvailable] = useState(false);
  const [shippingReady] = useState(true); // Shipping is always offered; hassle is handled by SRC supplies + labels

  // Packaging + supplies
  const [hasOriginalPackaging, setHasOriginalPackaging] = useState(null);
  const [sellerBuyingSupplies, setSellerBuyingSupplies] = useState(null);
  const [shippingCategories, setShippingCategories] = useState([]);
  const [selectedShippingCategory, setSelectedShippingCategory] = useState("");
  const [selectedShippingBox, setSelectedShippingBox] = useState("small");

  const [saving, setSaving] = useState(false);

  useEffect(() => {
    shippingAPI
      .getCategories()
      .then((r) => {
        const arr = Array.isArray(r.data) ? r.data : r.data?.categories || [];
        setShippingCategories(arr);
      })
      .catch(() => setShippingCategories([]));
  }, []);

  // Load existing listing when editing
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    marketplaceAPI
      .getListing(editId)
      .then((r) => {
        if (cancelled) return;
        const l = r.data || {};
        setForm((prev) => ({
          ...prev,
          title: l.title || "",
          description: l.description || "",
          category: l.category || "",
          brand: l.brand || "",
          model_name: l.model_name || "",
          condition: l.condition || "",
          price: l.price != null ? String(l.price) : "",
          currency: l.currency || "USD",
          whats_included: l.whats_included || "",
          reason_for_selling: l.reason_for_selling || "",
          year_purchased: l.year_purchased != null ? String(l.year_purchased) : "",
          original_price: l.original_price != null ? String(l.original_price) : "",
          seller_country: l.seller_country || "US",
          seller_zip_code: l.seller_zip_code || "",
          seller_city: l.seller_city || "",
          seller_state: l.seller_state || "",
          location: l.location || "",
        }));
        setImages(Array.isArray(l.images) ? l.images : []);
        setLocalPickupAvailable(!!l.local_pickup_available);
        if (typeof l.has_original_packaging === "boolean") setHasOriginalPackaging(l.has_original_packaging);
        if (l.shipping_category) setSelectedShippingCategory(l.shipping_category);
        if (l.shipping_box_size_type) setSelectedShippingBox(l.shipping_box_size_type);
        if (typeof l.seller_buying_supplies === "boolean") setSellerBuyingSupplies(l.seller_buying_supplies);
      })
      .catch(() => toast.error("Failed to load listing"))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isEdit, editId]);

  const brands = useMemo(() => brandsForCategory(form.category), [form.category]);
  const upd = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6 - images.length);
    const b64 = await Promise.all(files.map(fileToBase64));
    setImages((prev) => [...prev, ...b64]);
    e.target.value = "";
  };

  const shippingOption = shippingReady
    ? (localPickupAvailable ? "both" : "ship_only")
    : "local_only";

  const shippingSuppliesCost = useMemo(() => {
    const c = shippingCategories.find((x) => x.category_id === selectedShippingCategory);
    if (!c) return 0;
    return selectedShippingBox === "small" ? c.small_box?.supplies_cost || 0 : c.large_box?.supplies_cost || 0;
  }, [shippingCategories, selectedShippingCategory, selectedShippingBox]);

  const boxOptions = useMemo(() => {
    const c = shippingCategories.find((x) => x.category_id === selectedShippingCategory);
    const fmt = (b) => b ? `${b.length}×${b.width}×${b.height}" · up to ${b.weight}lb · $${Number(b.supplies_cost || 0).toFixed(2)} supplies` : "";
    return { small: fmt(c?.small_box), large: fmt(c?.large_box) };
  }, [shippingCategories, selectedShippingCategory]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.category || !form.condition || !form.price) {
      toast.error("Please fill in title, category, condition and price");
      return;
    }
    if (images.length === 0) {
      toast.error("Please add at least one photo");
      return;
    }
    if (!form.seller_zip_code.trim() || !form.seller_city.trim() || !form.seller_state.trim()) {
      toast.error("Please add your city, state/province and postal code so buyers know where the item ships from");
      return;
    }
    if (hasOriginalPackaging === null) {
      toast.error("Please indicate whether you have original packaging");
      return;
    }
    if (hasOriginalPackaging === false && sellerBuyingSupplies === null) {
      toast.error("Please choose whether you'll buy shipping supplies through SRC");
      return;
    }

    const locationString =
      form.seller_city && form.seller_state
        ? `${form.seller_city}, ${form.seller_state}`
        : form.location;

    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category,
        brand: form.brand || "",
        model_name: form.model_name || "",
        condition: form.condition,
        price: parseFloat(form.price),
        currency: form.currency,
        location: locationString,
        seller_country: form.seller_country,
        seller_zip_code: form.seller_zip_code.replace(/\s/g, ""),
        seller_city: form.seller_city,
        seller_state: form.seller_state,
        shipping_option: shippingOption,
        local_pickup_available: localPickupAvailable,
        images,
      };
      if (form.whats_included) payload.whats_included = form.whats_included;
      if (form.reason_for_selling) payload.reason_for_selling = form.reason_for_selling;
      if (form.year_purchased && !isNaN(+form.year_purchased)) payload.year_purchased = +form.year_purchased;
      if (form.original_price && !isNaN(+form.original_price)) payload.original_price = +form.original_price;

      if (hasOriginalPackaging !== null) {
        payload.has_original_packaging = hasOriginalPackaging;
      }
      if (selectedShippingCategory) {
        payload.shipping_category = selectedShippingCategory;
        payload.shipping_box_size_type = selectedShippingBox;
      }
      if (hasOriginalPackaging === false && sellerBuyingSupplies !== null) {
        payload.seller_buying_supplies = sellerBuyingSupplies;
        payload.supplies_available_to_buyer = !sellerBuyingSupplies;
      }

      const res = isEdit
        ? await marketplaceAPI.updateListing(editId, payload)
        : await marketplaceAPI.createListing(payload);
      const newId = editId || res.data?.id || res.data?.listing?.id;

      // If seller opted to buy supplies right now, launch Stripe checkout.
      if (!hasOriginalPackaging && sellerBuyingSupplies === true && newId) {
        try {
          const orderRes = await listingSuppliesAPI.orderSupplies(newId);
          if (orderRes.data?.already_paid) {
            toast.success(isEdit ? "Listing updated · supplies already paid" : "Listing published · supplies already paid");
            navigate(`/marketplace/${newId}`);
            return;
          }
          const paymentId = orderRes.data?.shipping_payment_id;
          if (!paymentId) throw new Error("Missing shipping payment id");
          const sess = await checkoutAPI.createSession("supplies", paymentId);
          const url = sess.data?.checkout_url;
          if (!url) throw new Error("Missing checkout url");
          toast.success(isEdit ? "Listing saved · redirecting to checkout" : "Listing published · redirecting to checkout");
          window.location.assign(url);
          return;
        } catch (err) {
          toast.error(err.response?.data?.detail || "Listing saved, but could not start supplies checkout. You can pay from the listing page.");
          navigate(`/marketplace/${newId}`);
          return;
        }
      }

      const modStatus = res.data?.moderation_status;
      if (modStatus === "pending") {
        toast.success("Your listing is pending review before it goes live. Sorry for any wait; we review quickly.");
        navigate("/my-listings");
        return;
      }
      toast.success(isEdit ? "Listing updated!" : "Listing published!");
      navigate(newId ? `/marketplace/${newId}` : "/marketplace");
    } catch (err) {
      toast.error(err.response?.data?.detail || (isEdit ? "Could not update listing" : "Could not create listing"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="mx-auto flex max-w-3xl items-center justify-center py-24 text-sm text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading listing…
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid={isEdit ? "edit-listing-page" : "create-listing-page"}>
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <h1 className="font-display text-2xl font-bold">{isEdit ? "Edit listing" : "Create a listing"}</h1>

      <form onSubmit={submit} className="space-y-5">
        {/* Photos */}
        <Card className="border-border bg-card p-5">
          <Label>Photos *</Label>
          <div className="mt-2 flex flex-wrap gap-3">
            {images.map((img, i) => (
              <div key={i} className="relative h-24 w-24 overflow-hidden rounded-md border border-border">
                <button
                  type="button"
                  onClick={() => setImages((p) => {
                    if (i === 0) return p;
                    const next = [...p];
                    const [chosen] = next.splice(i, 1);
                    next.unshift(chosen);
                    return next;
                  })}
                  className="block h-full w-full"
                  title={i === 0 ? "Cover photo" : "Set as cover"}
                  data-testid={`create-image-${i}`}
                >
                  <img src={img} alt="" className="h-full w-full object-cover" />
                </button>
                <button type="button" onClick={() => setImages((p) => p.filter((_, idx) => idx !== i))} className="absolute right-1 top-1 rounded-full bg-background/80 p-0.5" data-testid={`create-image-remove-${i}`}>
                  <X className="h-3 w-3" />
                </button>
                {i === 0 ? (
                  <span className="absolute bottom-0 left-0 right-0 bg-black/70 py-0.5 text-center text-[10px] font-bold text-white">Cover</span>
                ) : (
                  <span className="absolute bottom-0 left-0 right-0 bg-black/50 py-0.5 text-center text-[10px] text-white">Set cover</span>
                )}
              </div>
            ))}
            {images.length < 6 && (
              <label className="flex h-24 w-24 cursor-pointer flex-col items-center justify-center rounded-md border border-dashed border-border text-muted-foreground hover:border-primary hover:text-primary" data-testid="create-image-upload">
                <ImagePlus className="h-6 w-6" />
                <span className="mt-1 text-xs">Add</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={onFiles} />
              </label>
            )}
          </div>
        </Card>

        {/* Basic info */}
        <Card className="space-y-4 border-border bg-card p-5">
          <div className="space-y-2">
            <Label>Title *</Label>
            <Input value={form.title} onChange={(e) => upd("title")(e.target.value)} placeholder="e.g. Fanatec CSL DD 8Nm" data-testid="create-title-input" />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Category *</Label>
              <Select value={form.category} onValueChange={(v) => { upd("category")(v); upd("brand")(""); }}>
                <SelectTrigger data-testid="create-category-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Condition *</Label>
              <Select value={form.condition} onValueChange={upd("condition")}>
                <SelectTrigger data-testid="create-condition-select"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  {CONDITIONS.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      <span>{c.label}</span>
                      <span className="ml-2 text-xs text-muted-foreground">— {c.description}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {form.category && (
            <div className="space-y-2">
              <Label>Brand</Label>
              <Select value={form.brand} onValueChange={upd("brand")}>
                <SelectTrigger data-testid="create-brand-select"><SelectValue placeholder="Select a brand" /></SelectTrigger>
                <SelectContent>
                  {brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <ProductSelector
            manufacturer={form.brand}
            product={form.model_name}
            onChange={({ manufacturer, product }) =>
              setForm((f) => ({ ...f, brand: manufacturer || f.brand, model_name: product }))
            }
            testIdPrefix="create-product"
          />

          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2 sm:col-span-2">
              <Label>Price *</Label>
              <Input type="number" min="0" step="0.01" value={form.price} onChange={(e) => upd("price")(e.target.value)} placeholder="0.00" data-testid="create-price-input" />
            </div>
            <div className="space-y-2">
              <Label>Currency</Label>
              <Select value={form.currency} onValueChange={upd("currency")}>
                <SelectTrigger data-testid="create-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.keys(CURRENCY_SYMBOLS).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea rows={4} value={form.description} onChange={(e) => upd("description")(e.target.value)} placeholder="Describe the item, any wear, included accessories, mods, etc." data-testid="create-description-input" />
          </div>
        </Card>

        {/* Location */}
        <Card className="space-y-4 border-border bg-card p-5">
          <h3 className="flex items-center gap-2 font-display text-lg font-semibold uppercase"><MapPin className="h-4 w-4 text-primary" /> Location <span className="text-xs font-normal normal-case text-muted-foreground">(required — buyers see the ship-from location)</span></h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Country <span className="text-primary">*</span></Label>
              <Select value={form.seller_country} onValueChange={upd("seller_country")}>
                <SelectTrigger data-testid="create-country-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="US">United States</SelectItem>
                  <SelectItem value="CA">Canada</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Zip / Postal code <span className="text-primary">*</span></Label>
              <Input required value={form.seller_zip_code} onChange={(e) => upd("seller_zip_code")(e.target.value)} placeholder={form.seller_country === "US" ? "12345" : "A1A 1A1"} data-testid="create-zip-input" />
            </div>
            <div className="space-y-2">
              <Label>City <span className="text-primary">*</span></Label>
              <Input required value={form.seller_city} onChange={(e) => upd("seller_city")(e.target.value)} data-testid="create-city-input" />
            </div>
            <div className="space-y-2">
              <Label>State / Province <span className="text-primary">*</span></Label>
              <Input required value={form.seller_state} onChange={(e) => upd("seller_state")(e.target.value)} data-testid="create-state-input" />
            </div>
          </div>
        </Card>

        {/* Shipping & fulfillment */}
        <Card className="space-y-5 border-border bg-card p-5" data-testid="create-shipping-card">
          <h3 className="flex items-center gap-2 font-display text-lg font-semibold uppercase"><Truck className="h-4 w-4 text-primary" /> Shipping &amp; fulfillment</h3>

          {/* Local pickup */}
          <div>
            <Label>Offer local pickup?</Label>
            <RadioGroup value={String(localPickupAvailable)} onValueChange={(v) => setLocalPickupAvailable(v === "true")} className="mt-2 grid grid-cols-2 gap-3">
              <label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-3" data-testid="create-pickup-yes">
                <RadioGroupItem value="true" /> Yes, buyers can pick up
              </label>
              <label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-3" data-testid="create-pickup-no">
                <RadioGroupItem value="false" /> No, shipping only
              </label>
            </RadioGroup>
          </div>

          {/* Shipping-hassle helper */}
          <div className="rounded-md border border-primary/30 bg-primary/[0.06] px-4 py-3 text-sm">
            <p className="text-foreground/85">
              <strong>Shipping is always available on your listing.</strong> Buyers pay the shipping cost at checkout.
            </p>
            <Link
              to="/learn/marketplace#shipping-faq"
              className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
              data-testid="create-shipping-faq-link"
            >
              Worried about the hassle of shipping? See how SRC makes it easy →
            </Link>
          </div>

          {shippingReady && (
            <>
              {/* Packaging */}
              <div>
                <Label className="flex items-center gap-2"><Package className="h-3.5 w-3.5" /> Do you have the original packaging?</Label>
                <RadioGroup value={hasOriginalPackaging === null ? "" : String(hasOriginalPackaging)} onValueChange={(v) => setHasOriginalPackaging(v === "true")} className="mt-2 grid grid-cols-2 gap-3">
                  <label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-3" data-testid="create-og-yes">
                    <RadioGroupItem value="true" /> Yes, I have it
                  </label>
                  <label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-3" data-testid="create-og-no">
                    <RadioGroupItem value="false" /> No, need new packaging
                  </label>
                </RadioGroup>
              </div>

              {hasOriginalPackaging === false && (
                <>
                  <div className="rounded-md border border-primary/30 bg-primary/5 p-3 text-sm">
                    <p className="font-semibold">Get shipping supplies through SRC</p>
                    <p className="text-xs text-muted-foreground">We offer standardised boxes + packing material — buyers pay for it as part of checkout, or you can purchase now.</p>
                  </div>

                  {shippingCategories.length > 0 && (
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Shipping category</Label>
                        <Select value={selectedShippingCategory} onValueChange={setSelectedShippingCategory}>
                          <SelectTrigger data-testid="create-shipcat-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                          <SelectContent>
                            {shippingCategories.map((c) => (
                              <SelectItem key={c.category_id} value={c.category_id}>{c.category_name || c.category_id}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Box size</Label>
                        <Select value={selectedShippingBox} onValueChange={setSelectedShippingBox}>
                          <SelectTrigger data-testid="create-shipbox-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="small">Small{boxOptions.small ? ` — ${boxOptions.small}` : ""}</SelectItem>
                            <SelectItem value="large">Large{boxOptions.large ? ` — ${boxOptions.large}` : ""}</SelectItem>
                          </SelectContent>
                        </Select>
                        {(boxOptions.small || boxOptions.large) && (
                          <p className="text-[11px] text-muted-foreground" data-testid="create-shipbox-hint">
                            {selectedShippingBox === "small" ? boxOptions.small : boxOptions.large}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  <div>
                    <Label>Order shipping supplies now?</Label>
                    <RadioGroup value={sellerBuyingSupplies === null ? "" : String(sellerBuyingSupplies)} onValueChange={(v) => setSellerBuyingSupplies(v === "true")} className="mt-2 grid grid-cols-2 gap-3">
                      <label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-3" data-testid="create-supplies-buy">
                        <RadioGroupItem value="true" /> Yes, buy now {shippingSuppliesCost > 0 && `($${shippingSuppliesCost})`}
                      </label>
                      <label className="flex cursor-pointer items-center gap-2 rounded-md border border-border p-3" data-testid="create-supplies-later">
                        <RadioGroupItem value="false" /> No, I'll order later
                      </label>
                    </RadioGroup>
                    {sellerBuyingSupplies === true && (
                      <p className="mt-2 text-xs text-primary" data-testid="create-supplies-buy-hint">
                        You&apos;ll be taken to secure checkout right after saving the listing.
                      </p>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </Card>

        {/* Optional details */}
        <Card className="space-y-4 border-border bg-card p-5">
          <h3 className="font-display text-lg font-semibold uppercase">Optional details</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Year purchased</Label>
              <Input value={form.year_purchased} onChange={(e) => upd("year_purchased")(e.target.value)} placeholder="e.g. 2023" data-testid="create-year-input" />
            </div>
            <div className="space-y-2">
              <Label>Original retail price</Label>
              <Input type="number" step="0.01" value={form.original_price} onChange={(e) => upd("original_price")(e.target.value)} placeholder="e.g. 899.00" data-testid="create-original-input" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>What's included</Label>
            <Input value={form.whats_included} onChange={(e) => upd("whats_included")(e.target.value)} placeholder="Cables, mounts, spare parts..." data-testid="create-included-input" />
          </div>
          <div className="space-y-2">
            <Label>Reason for selling</Label>
            <Input value={form.reason_for_selling} onChange={(e) => upd("reason_for_selling")(e.target.value)} data-testid="create-reason-input" />
          </div>
        </Card>

        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <ShieldCheck className="h-4 w-4 text-primary" /> Buyer payment is held securely until delivery is confirmed.
        </p>

        <Button type="submit" disabled={saving} className="w-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="create-submit-button">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (isEdit ? "Save changes" : "Publish listing")}
        </Button>
      </form>
    </div>
  );
}
